import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "onClick", "children", "tooltipLabel", "tooltipPlacement", "dataTestId"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import BaseButton from './BaseButton';
import Tooltip from './Tooltip';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Whether the button shows text of its own. An icon child is an element and
// aria-hidden; a string or number child is what the user reads on the button.
var hasVisibleText = function hasVisibleText(children) {
  return React.Children.toArray(children).some(function (child) {
    return typeof child === 'string' || typeof child === 'number';
  });
};

// The ref reaches the <button>, for a caller that moves focus to it.
var IconButton = /*#__PURE__*/forwardRef(function IconButton(_ref, ref) {
  var className = _ref.className,
    onClick = _ref.onClick,
    children = _ref.children,
    tooltipLabel = _ref.tooltipLabel,
    tooltipPlacement = _ref.tooltipPlacement,
    dataTestId = _ref.dataTestId,
    props = _objectWithoutProperties(_ref, _excluded);
  var descriptionId = useId();
  if (!tooltipLabel) {
    return /*#__PURE__*/_jsx(BaseButton, _objectSpread(_objectSpread({
      ref: ref,
      onClick: onClick,
      className: twMerge('hover:darken-10 border-none', className),
      "data-test-id": dataTestId
    }, props), {}, {
      children: children
    }));
  }

  // The tooltip is a hover affordance: its text lives in a portal that only
  // mounts on mouseenter, so it can never name the button for a keyboard or
  // screen-reader user. An icon-only button holds nothing but an
  // (aria-hidden) icon, so without an aria-label it announces as a bare
  // "button". A button with visible text already has its name, and the
  // tooltip is a reason rather than a name: giving it aria-label would make
  // a button that reads "Ask to join" announce as "Waiting for host" (WCAG
  // 2.5.3, Label in Name). That text becomes the description instead,
  // carried by a visually hidden span so it is there whether or not the
  // tooltip is open and even when the button is disabled and cannot be
  // hovered. The span sits next to the button, not inside it: a button is
  // named from its contents, hidden or not, so inside it would still be
  // read as part of the name. An explicit aria-label from the call site
  // still wins.
  var labelledByText = hasVisibleText(children);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip, {
      label: tooltipLabel,
      placement: tooltipPlacement,
      children: /*#__PURE__*/_jsx(BaseButton, _objectSpread(_objectSpread({
        ref: ref,
        onClick: onClick,
        className: twMerge('hover:darken-10 border-none', className),
        "data-test-id": dataTestId,
        "aria-label": labelledByText ? undefined : tooltipLabel,
        "aria-describedby": labelledByText ? descriptionId : undefined
      }, props), {}, {
        children: children
      }))
    }), labelledByText && /*#__PURE__*/_jsx("span", {
      id: descriptionId,
      className: "sr-only",
      children: tooltipLabel
    })]
  });
});
export default IconButton;